/**
 * ## Logger
 *
 * Client side logging tool with possibilities to use with custom logging implementations.
 *
 * Comes bundled with a console logger, which is the default logger.
 * The default configuration is set up to only log errors.
 *
 * In addition to being able to extract it like `import { logger } from '@pbs/ngen-assets';`, it also exposes `logger` in the
 * `window` object, so it can be reached in console by just writing `logger` or `window.logger`.
 *
 * If setting a custom logging implementation, the implementation must expose a `log` method. When a message is let
 * through, this method will be invoked with the following three arguments:
 *
 * * `namespace` {String} the current logger namespace
 * * `logLevel` {String} the log level ('debug', 'info', 'warn' or 'error')
 * * `messages` {[Arguments](https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Functions/arguments)} the messages for logging
 *
 * The `log` method will only be invoked if the log level is set higher than, or equal to, the level of the message.
 *
 * When setting log level through a configuration or the method `setGlobalLogLevel`, always use the levels exposed on this logger object:
 *
 * * logger.ALL
 * * logger.DEBUG
 * * logger.INFO
 * * logger.WARN
 * * logger.ERROR
 * * logger.NONE
 *
 * It is possible to use several loggers for one instantiation. This is done simply by setting the configuration.logger property
 * to an array of loggers (see example below). As a convenience, the bundled console logger is available as `logger.loggerTypes.CONSOLE`.
 *
 *
 * ### Configuration
 *
 * This module requires an `Object` for initialization which contains:
 *
 * | Parameter       | Type   | Default value | Description |
 * | ---             | ---    | ---           | --- |
 * | defaultLogLevel | string | 'ERROR'       | Default log level for created loggers. Possible values are 'ALL', 'DEBUG', 'INFO', 'WARN', 'ERROR' and 'NONE'. |
 * | defaultLoggers  | array  | ['CONSOLE']   | Default loggers for created loggers to use. The only option is 'CONSOLE'. |
 *
 * > Changing default values applies to loggers already instantiated, but if any messages are logged prior to this, default values will apply to them.
 *
 * ### Examples
 *
 * #### Interface
 *
 *     import { logger } from '@pbs/ngen-assets';
 *
 * #### Get logger with default configuration (log level `error` and console logger)
 *
 *     const myDefaultLogger = logger.get('my-default-logger');
 *
 * #### Alternatively, configure log level to `warn` when getting it
 *
 *     const myDefaultLogger = logger.get('my-default-logger', {
 *         level: logger.WARN
 *     });
 *
 * #### Log one message of each type on `myDefaultLogger`
 *
 *     myDefaultLogger.debug('All is fine, logger created');
 *     myDefaultLogger.info('All is good');
 *     myDefaultLogger.warn('Your device is running out of memory!');
 *     myDefaultLogger.error('Critical error!');
 *     // Only `warn` and `error` will be logged if level was set to `warn` as previous example,
 *     // otherwise `error` which is the default level - unless overridden
 *
 * #### Configure the logger to set log level to `info`
 *
 *     logger.configureLogger('my-default-logger', {
 *         level: logger.INFO
 *     });
 *
 * #### Get logger with custom implementation, logging in memory on `error`
 *
 *     const myMemoryLoggerImplementation = {
 *         memoryLog: [],
 *         log: function (namespace, logLevel, messages) {
 *             this.memoryLog.push(`${namespace}: ${logLevel.toUpperCase()} - ${messages.join(', ')}`);
 *         }
 *     };
 *
 *     const myMemoryLogger = logger.get('my-memory-logger', {
 *         logger: myMemoryLoggerImplementation,
 *         level: logger.ERROR
 *     });
 *
 * #### Add memoryLogger to be used as default logger, additionally to console logger
 *
 *     logger.addDefaultLogger(myMemoryLogger);
 *
 *
 * #### Using both console logger and memory logger
 *
 *     const myDualChannelLogger = logger.get('my-dual-logger', {
 *         logger: [
 *             myMemoryLoggerImplementation,
 *             logger.loggerTypes.CONSOLE
 *         ],
 *     });
 *
 * #### Set global log level to `info`
 *
 *     logger.setGlobalLogLevel(logger.INFO);
 * @module module:modules/logger
 *
 * @public
 */
import { x } from '../utils';
import consoleLogger from '../logger-types/console';

/* eslint-disable no-console */

const logLevels = {
    ALL: 'all',
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
    NONE: 'none',
};
const logLevelsPrio = [
    logLevels.ALL,
    logLevels.DEBUG,
    logLevels.INFO,
    logLevels.WARN,
    logLevels.ERROR,
    logLevels.NONE,
];

const defaultLoggerType = consoleLogger;
const loggerTypes = {
    CONSOLE: consoleLogger,
};

const defaultLogLevel = logLevels.ERROR;
const defaultLoggerConfiguration = {
    level: defaultLogLevel,
    _logLevelDefault: true,
    logger: defaultLoggerType,
};

const loggers = {};
let listeners = [];

function getLogLevelByString(logLevelString) {
    logLevelString = logLevelString.toUpperCase();
    if (logLevels.hasOwnProperty(logLevelString)) {
        return logLevels[logLevelString];
    }
}

function getLoggersByNames(loggerArray) {
    if (Array.isArray(loggerArray)) {
        const matchingLoggers = [];

        loggerArray.forEach(loggerName => {
            const loggerKey = loggerName.toUpperCase();
            if (loggerTypes.hasOwnProperty(loggerKey)) {
                matchingLoggers.push(loggerTypes[loggerKey]);
            }
        });

        if (matchingLoggers.length) {
            return matchingLoggers.length > 1 ? matchingLoggers : matchingLoggers[0];
        }
    } else if (typeof loggerArray === 'string') {
        const loggerKey = loggerArray.toUpperCase();
        if (loggerTypes.hasOwnProperty(loggerKey)) {
            return loggerTypes[loggerKey];
        }
    }
    return null;
}

function validateLogLevel(configuration, namespace) {
    let isValidLogLevel = true;
    // If configuration with level specified, need to validate it
    if (
        configuration &&
        configuration.level &&
        !logLevelsPrio.find(level => level === configuration.level)
    ) {
        isValidLogLevel = false;
        let availableLogLevelsList = 'import loggerFactory from "logger";\n';
        Object.getOwnPropertyNames(logLevels).forEach(
            logLevelName => (availableLogLevelsList += `  loggerFactory.${logLevelName};\n`)
        );
        // Output error message
        const logLevelErrorMessage = `Log level '${configuration.level}' is not available. Log level will remain as before. Available log levels are:
${availableLogLevelsList}`;
        console.error(logLevelErrorMessage);

        // Set log level to what is were before for the namespace, or to default level
        if (namespace) {
            configuration.level =
                (loggers[namespace] && loggers[namespace].level) ||
                defaultLoggerConfiguration.level;
        }
    }
    return isValidLogLevel;
}

const loggerFactory = {
    /**
     * Initialises and potentially overrides default log level and default loggers. If logger instances with default
     * loggers and/or log level exist, they will be updated if these are overridden through configuration.
     *
     * @param {String} config The configuration of logger
     * @param {String} config.defaultLogLevel Default threshold log level. Optional. Default is `ERROR`
     * @param {Array|String} config.defaultLoggers Default logger(s) to use. Optional. Default is `['CONSOLE']`
     *
     * @memberof module:modules/logger
     *
     * @function init
     *
     * @instance
     *
     * @private
     */
    init: function (config) {
        config = config || { defaultLogLevel: 'ERROR', defaultLoggers: 'CONSOLE' };

        const level =
            (config.defaultLogLevel && getLogLevelByString(config.defaultLogLevel)) ||
            defaultLoggerConfiguration.level;
        defaultLoggerConfiguration.level = level;
        const logger =
            (config.defaultLogLevel && getLoggersByNames(config.defaultLoggers)) ||
            defaultLoggerConfiguration.logger;
        defaultLoggerConfiguration.logger = logger;
        // Update all loggers using the default log level
        this.loggers().forEach(namespace => {
            if (loggers[namespace]._logLevelDefault) {
                loggers[namespace].level = level;
            }
            if (!loggers[namespace].logger) {
                loggers[namespace].logger = logger;
            }
        });
    },

    /**
     * Enabler additional default logger.
     * Logger should implement method `log` which takes three parameter: namespace, logLevel, messages.
     * For example,
     *
     * const myMemoryLoggerImplementation = {
     *     memoryLog: [],
     *     log: function (namespace, logLevel, messages) {
     *         const messagesArray = Array.prototype.slice.call(messages);
     *         this.memoryLog.push(`${namespace}: ${logLevel.toUpperCase()} - ${messagesArray.join(', ')}`);
     *     }
     * };
     * logger.addDefaultLogger(myMemoryLoggerImplementation);
     *
     * @param {Object} logger The configuration of logger
     *
     * @memberof module:modules/logger
     *
     * @function addDefaultLogger
     *
     * @instance
     *
     * @public
     */
    addDefaultLogger: function (logger) {
        if (!Array.isArray(defaultLoggerConfiguration.logger)) {
            defaultLoggerConfiguration.logger = [defaultLoggerConfiguration.logger];
        }
        defaultLoggerConfiguration.logger.push(logger);
    },

    /**
     * Instantiates a new, alternatively returns an existing, logger with the given namespace. If sending in a configuration,
     * it will override existing configuration properties. If no configuration is sent in, the default configuration will be used.
     *
     * The returned object has methods for the available log levels; `debug`, `info`, `warn` and `error`, which can be used for logging messages.
     *
     * @example
     * // Default logger
     * const myDefaultLogger = logger.get('my-default-logger');
     *
     * @example
     * // Logger with log level set to `warn`
     * const myWarnLogger = logger.get('my-warn-logger', {
     *     level: logger.WARN
     * });
     *
     * @example
     * // Logger using custom log implementation
     * const myCustomLoggerImplementation = {
     *     log: function (namespace, logLevel, messages) {
     *         // Custom log functionality
     *     }
     * };
     *
     * const myCustomLogger = logger.get('my-custom-logger', {
     *     logger: myCustomLoggerImplementation
     * });
     *
     * @example
     * // Logger using above defined log implementation and bundled console logger
     * const myDualLogger = logger.get('my-dual-logger', {
     *     logger: [
     *         logger.loggerTypes.CONSOLE,
     *         myCustomLoggerImplementation
     *     ]
     * });
     *
     * @param {String} namespace The namespace for the instantiated logger
     * @param {Object} configuration Configuration for the logger. Optional
     * @param {Object} configuration.level Threshold log level for this logger. Optional. Default is `error` unless overridden in configuration.
     * @param {Object|Object[]} configuration.logger Logging method(s) for this logger. Optional. Default is the bundled console logger.
     *
     * @return {Object} A logger instance, with methods for `debug`, `info`, `warn` and `error` exposed, all of which takes log messages as parameters.
     *
     * @memberof module:modules/logger
     *
     * @see module:modules/logger/loggers/console-logger
     *
     * @function get
     *
     * @instance
     *
     * @public
     */
    get: function (namespace, configuration) {
        configuration = configuration || {};
        if (configuration.level) {
            configuration._logLevelDefault = false;
        }
        if (!loggers.hasOwnProperty(namespace)) {
            validateLogLevel(configuration, namespace);
            loggers[namespace] = x.defaults(configuration, defaultLoggerConfiguration);
        } else {
            this.configureLogger(namespace, configuration);
        }

        const loggerForNamespace = {};
        Object.getOwnPropertyNames(logLevels).forEach(logLevelKey => {
            const logLevel = logLevels[logLevelKey];
            if (typeof logLevel === 'string') {
                loggerForNamespace[logLevel] = function (...args) {
                    const namespaceLogLevelPrio = logLevelsPrio.indexOf(loggers[namespace].level);
                    const thisLogLevelPrio = logLevelsPrio.indexOf(logLevel);
                    if (thisLogLevelPrio >= namespaceLogLevelPrio) {
                        let loggersForNamespace = loggers[namespace].logger;
                        if (!Array.isArray(loggersForNamespace)) {
                            loggersForNamespace = [loggersForNamespace];
                        }
                        loggersForNamespace.forEach(logger => {
                            logger.log(namespace, logLevel, args);
                            loggerFactory.getListeners().forEach(listener => {
                                listener(namespace, logLevel, args);
                            });
                        });
                    }
                };
            }
        });
        return loggerForNamespace;
    },

    /**
     * Adds listener functions that gets called on every log.
     *
     * @param {Function} listener The listener function.
     *
     * @memberof module:modules/logger
     *
     * @function addListener
     *
     * @instance
     *
     * @public
     */
    addListener: function (listener) {
        if (typeof listener !== 'function') {
            console.error(
                "The listener must be a function. Passed lister's type is  -",
                typeof listener
            );
            return;
        }
        listeners.push(listener);
    },

    /**
     * Removes listener by reference.
     *
     * @param {Function} listener The listener function to remove.
     *
     * @memberof module:modules/logger
     *
     * @function removeListener
     *
     * @instance
     *
     * @public
     */
    removeListener: listener => {
        listeners = listeners.filter(l => l !== listener);
    },

    /**
     * Returns all listeners.
     *
     * @memberof module:modules/logger
     *
     * @function getListeners
     *
     * @returns {function[]} listeners
     *
     * @instance
     *
     * @public
     */
    getListeners: () => listeners,

    /**
     * Configures an existing logger with the given namespace. The sent in configuration will override existing configuration properties.
     *
     * @example
     * // Change log level to `warn` for 'my-warn-logger'
     * logger.configureLogger('my-warn-logger', {
     *     level: logger.WARN
     * });
     *
     * @example
     * // Change logger to a custom log implementation for 'my-custom-logger'
     * const myCustomLoggerImplementation = {
     *     log: function (namespace, logLevel, messages) {
     *         // Custom log functionality
     *     }
     * };
     *
     * logger.configureLogger('my-custom-logger', {
     *     logger: myCustomLoggerImplementation
     * });
     *
     * @example
     * // Change logger to both above custom logger as well as bundled console logger
     * logger.configureLogger('my-custom-logger', {
     *     logger: [
     *         myCustomLoggerImplementation,
     *         logger.loggerTypes.CONSOLE
     *     ]
     * });
     *
     * @param {String} namespace The namespace for the logger to configure
     * @param {Object} configuration Configuration for the logger
     * @param {Object} configuration.level Threshold log level for this logger. Optional
     * @param {Object|Object[]} configuration.logger Logging method(s) for this logger. Optional
     *
     * @memberof module:modules/logger
     *
     * @function configureLogger
     *
     * @instance
     *
     * @public
     */
    configureLogger: (namespace, configuration) => {
        if (loggers.hasOwnProperty(namespace)) {
            if (configuration.level) {
                configuration._logLevelDefault = false;
            }
            validateLogLevel(configuration, namespace);
            loggers[namespace] = x.defaults({}, configuration, loggers[namespace]);
        }
    },

    /**
     * Sets a log level for all currently instantiated loggers, and as the default log level for future created loggers.
     *
     * @example
     * // Change global log level to `info`
     * logger.setGlobalLogLevel(logger.INFO);
     *
     * @example
     * // Change global log level to `all`
     * logger.setGlobalLogLevel(logger.ALL);
     *
     * @example
     * // Change global log level to `none`
     * logger.setGlobalLogLevel(logger.NONE);
     *
     * @param {Object} logLevel Threshold log level for all instantiated loggers
     *
     * @memberof module:modules/logger
     *
     * @function setGlobalLogLevel
     *
     * @instance
     *
     * @public
     */
    setGlobalLogLevel: function (logLevel) {
        const logLevelConfiguration = { level: logLevel, _logLevelDefault: false };
        if (validateLogLevel(logLevelConfiguration)) {
            defaultLoggerConfiguration.level = logLevelConfiguration.level;
            this.loggers().forEach(namespace => {
                this.configureLogger(namespace, logLevelConfiguration);
            });
        }
    },

    /**
     * Gives a list of all namespaces for currently instantiated loggers.
     *
     * @example
     * let instantiatedLoggers = logger.loggers(); // []
     *
     * // Add two loggers
     * logger.get('my-first-logger');
     * logger.get('my-second-logger');
     *
     * instantiatedLoggers = logger.loggers(); // ['my-first-logger', 'my-second-logger']
     *
     * @memberof module:modules/logger
     *
     * @function loggers
     *
     * @returns {string[]} loggers:
     *
     * @instance
     *
     * @public
     */
    loggers: () => Object.getOwnPropertyNames(loggers),

    /**
     * @memberof module:modules/logger
     *
     * @description An object containing all available bundled loggers to use (right now, console loggers).
     *
     * @instance
     *
     * @public
     */
    loggerTypes: loggerTypes,

    /**
     * @memberof module:modules/logger
     *
     * @description Log Level `all`. If used, all log messages will be fired.
     *
     * @instance
     *
     * @public
     */
    ALL: logLevels.ALL,

    /**
     * @memberof module:modules/logger
     *
     * @description Log Level `debug`. If used, all log messages will be fired.
     *
     * @instance
     *
     * @public
     */
    DEBUG: logLevels.DEBUG,

    /**
     * @memberof module:modules/logger
     *
     * @description Log Level `info`. If used, log messages of level `info`, `warn` and `error` will be fired.
     *
     * @instance
     *
     * @public
     */
    INFO: logLevels.INFO,

    /**
     * @memberof module:modules/logger
     *
     * @description Log Level `warn`. If used, log messages of level `warn` and `error` will be fired.
     *
     * @instance
     *
     * @public
     */
    WARN: logLevels.WARN,

    /**
     * @memberof module:modules/logger
     *
     * @description Log Level `error`. If used, only log messages of level `error` will be fired.
     *
     * @instance
     *
     * @public
     */
    ERROR: logLevels.ERROR,

    /**
     * @memberof module:modules/logger
     *
     * @description Log Level `none`. If used, no log messages will be fired.
     *
     * @instance
     *
     * @public
     */
    NONE: logLevels.NONE,

    instrumentation: () => ({ defaultLoggerConfiguration: defaultLoggerConfiguration }),
};

window.logger = loggerFactory;

export default loggerFactory;
